import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Copy, Paintbrush, Eye, EyeOff, Check, SearchCheck } from 'lucide-react';
import { themesApi, adminManagementApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { usePreferences } from '../../context/PreferencesContext';
import { generateTokens, DEFAULT_SEEDS } from '../../theme/themeGenerator';
import ThemePreview from './themes/ThemePreview';
import ThemeSwatchRow from './themes/ThemeSwatchRow';
import useThemeGroups from './themes/useThemeGroups';
import ThemeBuilder from './themes/ThemeBuilder';
import { DeleteThemeDialog, ApplyToDialog } from './themes/ThemeDialogs';

const MAX_COMPARE = 3;

const errText = (e) => {
  const d = e?.response?.data;
  if (typeof d === 'string') return d;
  return d?.error || d?.message || e?.message || 'Request failed';
};

const snapshotOf = (b) => JSON.stringify({
  key: b.key, label: b.label, category: b.category, organizationId: b.organizationId, base: b.base, status: b.status, seeds: b.seeds, tokens: b.tokens,
});

const builderFromTheme = (t, mode) => ({
  mode,
  originalKey: t.themeKey,
  key: mode === 'create' ? '' : t.themeKey,
  label: mode === 'create' ? `${t.label} Copy` : t.label,
  category: t.category || '',
  organizationId: t.organizationId ?? null,
  base: t.base || 'Light',
  status: mode === 'create' ? 'Draft' : t.status,
  isSystem: mode === 'edit' && !!t.isSystem,
  originalStatus: mode === 'edit' ? t.status : null,
  seeds: { ...DEFAULT_SEEDS, ...(t.seeds || {}), base: String(t.base || 'Light').toLowerCase() },
  tokens: { ...(t.tokens || {}) },
});

const blankBuilder = (themes) => {
  const std = themes.find(t => t.themeKey === 'standard');
  const seeds = { ...DEFAULT_SEEDS, ...(std?.seeds || {}), base: 'light', overrides: [] };
  return {
    mode: 'create', originalKey: null, key: '', label: '', category: '', organizationId: null, base: 'Light', status: 'Draft',
    seeds, tokens: generateTokens(seeds, {}),
  };
};

/**
 * Preferences > Themes, the SysAdmin workbench (everyone else picks a theme on Display).
 * Browsing, comparing and trying are free; a try-on lives in PreferencesContext (footer chip)
 * so it survives navigation until stopped or applied at the Apply to scope. With the page
 * pencil on, "Use this theme" picks it the same way the Display picker does, and the builder
 * popup manages the theme table with its own Save.
 */
const ThemesTab = ({
  isEditing, onBuilderDirtyChange, themeDef, selectedKey, onStageTheme, renderScope, themeScopeTarget = 'global',
}) => {
  const { currentUser, isSysAdmin } = useAuth();
  const { notify } = useNotifications();
  const { themes: ctxThemes, refreshThemes, trial, startTrial, stopTrial } = usePreferences();
  const themes = useMemo(() => ctxThemes || [], [ctxThemes]);
  // The API ignores ?userId= when auth is on; with auth off it identifies the SysAdmin.
  const uid = currentUser?.id;

  const [compare, setCompare] = useState(null);       // null until seeded from the selection
  const [builder, setBuilder] = useState(null);
  const [builderBase, setBuilderBase] = useState('');
  const [builderError, setBuilderError] = useState(null);
  const [savingTheme, setSavingTheme] = useState(false);
  const [dialog, setDialog] = useState(null);         // { type, theme }
  const [activeId, setActiveId] = useState(null);    // sample swatch the toolbar acts on
  const [analyze, setAnalyze] = useState(false);   // contrast analysis: off each time the tab opens
  const [previewPage, setPreviewPage] = useState('grid');   // one sample page across all compare columns
  const [organizations, setOrganizations] = useState([]); // [{ id, displayName }] for the builder's org picker

  const builderDirty = !!builder && snapshotOf(builder) !== builderBase;
  const canManage = isSysAdmin && isEditing;
  const { groups, categories } = useThemeGroups(themes);

  // Seed the compare set with the current selection once themes arrive.
  useEffect(() => {
    if (compare !== null || themes.length === 0) return;
    const first = themes.find(t => t.themeKey === selectedKey) || themes[0];
    setCompare(first ? [first.themeKey] : []);
  }, [compare, themes, selectedKey]);

  // Drop compare entries whose theme was deleted or retired out from under us.
  useEffect(() => {
    if (compare === null) return;
    const keys = new Set(themes.map(t => t.themeKey));
    if (compare.some(k => !keys.has(k))) setCompare(compare.filter(k => keys.has(k)));
  }, [themes, compare]);

  // Orgs for scoping a theme; only the builder needs them, and only SysAdmins open it.
  useEffect(() => {
    if (!isSysAdmin) return undefined;
    let live = true;
    adminManagementApi.getOrganizations()
      .then(res => { if (live) setOrganizations((res.data || []).filter(o => o.isActive !== false)); })
      .catch(() => { /* picker falls back to All organizations only */ });
    return () => { live = false; };
  }, [isSysAdmin]);

  // Report unsaved builder work to the page so Cancel can warn.
  useEffect(() => { onBuilderDirtyChange?.(builderDirty); }, [builderDirty, onBuilderDirtyChange]);
  useEffect(() => () => onBuilderDirtyChange?.(false), [onBuilderDirtyChange]);

  // Leaving edit mode closes the builder (the page already confirmed discarding).
  useEffect(() => { if (!isEditing) { setBuilder(null); setBuilderError(null); } }, [isEditing]);

  const themeByKey = useMemo(() => {
    const m = {};
    themes.forEach(t => { m[t.themeKey] = t; });
    return m;
  }, [themes]);

  // The contrast results live on the Status page, so turning analysis on goes there.
  const toggleAnalyze = (on) => { setAnalyze(on); if (on) setPreviewPage('status'); };

  const toggleCompare = (key) => {
    setCompare(prev => {
      const cur = prev || [];
      if (cur.includes(key)) return cur.filter(k => k !== key);
      return cur.length >= MAX_COMPARE ? [...cur.slice(1), key] : [...cur, key];
    });
  };

  // ── Builder ─────────────────────────────────────────────────────────────
  const openBuilder = (b) => {
    setBuilder(b);
    setBuilderBase(snapshotOf(b));
    setBuilderError(null);
  };

  const guardBuilder = (then) => {
    if (!builderDirty) { then(); return; }
    notify.confirm('Discard unsaved theme changes?', then, { confirmText: 'Discard', cancelText: 'Keep Editing' });
  };

  const closeBuilder = () => guardBuilder(() => {
    setBuilder(null);
    setBuilderError(null);
  });

  const saveTheme = () => {
    if (!builder) return;
    const retiring = builder.mode === 'edit' && builder.status === 'Retired' && builder.originalStatus !== 'Retired';
    if (!retiring) { writeTheme(); return; }
    notify.confirm(
      `Retire "${builder.label.trim()}"? It disappears from every picker and anyone using it falls back to Standard. Their selections are kept, so making it Active again restores them.`,
      writeTheme,
      { confirmText: 'Retire', cancelText: 'Keep Editing' },
    );
  };

  const writeTheme = async () => {
    setSavingTheme(true);
    setBuilderError(null);
    const seeds = { ...builder.seeds, base: builder.base.toLowerCase() };
    try {
      let key = builder.originalKey;
      if (builder.mode === 'create') {
        // The API assigns the key; the builder adopts it so later saves are updates.
        const res = await themesApi.create({
          label: builder.label.trim(), category: builder.category, organizationId: builder.organizationId, base: builder.base,
          seeds, tokens: builder.tokens, status: builder.status,
        }, uid);
        key = res.data.themeKey;
      } else {
        // organizationId 0 = every user (the API reads null as "leave alone")
        await themesApi.update(key, {
          label: builder.label.trim(), category: builder.category, organizationId: builder.organizationId ?? 0, base: builder.base,
          seeds, tokens: builder.tokens, status: builder.status,
        }, uid);
      }
      await refreshThemes();
      const saved = { ...builder, mode: 'edit', key, originalKey: key, originalStatus: builder.status, seeds };
      setBuilder(saved);
      setBuilderBase(snapshotOf(saved));
      notify.success(`Theme "${builder.label.trim()}" saved.`);
    } catch (e) {
      setBuilderError(errText(e));
    } finally {
      setSavingTheme(false);
    }
  };

  const startEdit = (t) => guardBuilder(() => openBuilder(builderFromTheme(t, 'edit')));
  const startClone = (t) => guardBuilder(() => openBuilder(builderFromTheme(t, 'create')));
  const startNew = () => guardBuilder(() => openBuilder(blankBuilder(themes)));

  // Popup footer lifecycle actions act on the saved theme behind the open builder.
  const builderTheme = builder && builder.mode === 'edit' ? themes.find(x => x.themeKey === builder.originalKey) : null;

  const dialogDone = async (afterKey) => {
    setDialog(null);
    await refreshThemes();
    if (builder && dialog && builder.originalKey === dialog.theme.themeKey) setBuilder(null);
    if (afterKey) notify.success('Done.');
  };

  // ── Sample swatches ─────────────────────────────────────────────────────
  const columns = useMemo(() => (compare || [])
    .map(k => themeByKey[k])
    .filter(Boolean)
    .slice(0, MAX_COMPARE)
    .map(t => ({ id: t.themeKey, key: t.themeKey, label: t.label, tokens: t.tokens, theme: t })), [compare, themeByKey]);

  // The toolbar acts on the active sample swatch; it falls back to the first one shown.
  const active = columns.find(c => c.id === activeId) || columns[0] || null;
  const activeTheme = active?.theme || null;

  const badgesFor = (t) => (
    <>
      {t.status === 'Draft' && <span className="stui-badge-orange">Draft</span>}
      {t.status === 'Retired' && <span className="stui-badge-gray">Retired</span>}
      {t.themeKey === selectedKey && <span className="stui-badge-primary">Current</span>}
    </>
  );

  const clickListLabel = (e, key) => {
    e.preventDefault();
    if (!(compare || []).includes(key)) toggleCompare(key);
    setActiveId(key);
  };

  // Eye, then the builder actions, then picking: Use this theme and its Apply to scope.
  const renderToolbar = () => {
    const t = activeTheme;
    const trying = !!trial;
    const isRetired = t?.status === 'Retired';
    return (
      <div className="stui-theme-toolbar">
        {trying ? (
          <button type="button" className="stui-crud-btn active" title={`Stop trying ${trial.label}`} onClick={stopTrial}><EyeOff size={14} /></button>
        ) : (
          <button
            type="button"
            className="stui-crud-btn"
            title="Try on the whole app (not saved; stop or apply it from the footer)"
            disabled={!t}
            onClick={() => startTrial({ key: t.themeKey, label: t.label, tokens: t.tokens, scope: themeScopeTarget })}
          >
            <Eye size={14} />
          </button>
        )}
        {canManage && (
          <>
            <button type="button" className="stui-crud-btn" title="Edit this theme" disabled={!t} onClick={() => startEdit(t)}><Paintbrush size={14} /></button>
            <button type="button" className="stui-crud-btn" title="New theme" onClick={startNew}><Plus size={14} /></button>
            <button type="button" className="stui-crud-btn" title="Clone to a new theme" disabled={!t} onClick={() => startClone(t)}><Copy size={13} /></button>
          </>
        )}
        {isEditing && themeDef && (
          <>
            <span className="stui-theme-toolbar-sep" />
            <button
              type="button"
              className="stui-crud-btn"
              title={t && selectedKey === t.themeKey ? 'Already your selected theme' : 'Use this theme'}
              disabled={!t || isRetired || selectedKey === t.themeKey}
              onClick={() => onStageTheme(t.themeKey)}
            >
              <Check size={14} />
            </button>
            {renderScope?.()}
          </>
        )}
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (ctxThemes == null) {
    return <div className="stui-label-note">Loading themes...</div>;
  }
  if (themes.length === 0) {
    return <div className="stui-label-note stui-text-error">No themes are available. Check the connection and reload.</div>;
  }

  return (
    <>
      <div className="stui-theme-hintbar">
        <span className="stui-ml-auto" />
        {renderToolbar()}
      </div>

      <div className="stui-theme-tab">
        <div className="stui-theme-list">
          <div className="stui-detail-section-header stui-theme-section-header">
            <span>Themes</span>
            <button
              type="button"
              className={`stui-related-header-btn${analyze ? ' active' : ''}`}
              title={analyze ? 'Hide contrast analysis' : 'Analyze contrast on the sample swatches'}
              onClick={() => toggleAnalyze(!analyze)}
            >
              <SearchCheck size={16} />
            </button>
          </div>
          {groups.map(g => (
            <React.Fragment key={g.category}>
              <div className="stui-theme-subhead">{g.category}</div>
              {g.themes.map(t => {
                const checked = (compare || []).includes(t.themeKey);
                const isActive = !!active && active.id === t.themeKey;
                return (
                  <label key={t.themeKey} className={`stui-theme-list-item${checked ? ' checked' : ''}${isActive ? ' active' : ''}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCompare(t.themeKey)} />
                    <ThemeSwatchRow tokens={t.tokens} />
                    <span className="stui-theme-list-label" onClick={e => clickListLabel(e, t.themeKey)}>
                      {t.label}
                    </span>
                    {badgesFor(t)}
                  </label>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        <div className="stui-theme-columns">
          {columns.length === 0 && <div className="stui-label-note">Tick a theme in the list to show its sample swatch.</div>}
          {columns.map(col => (
            // The whole sample swatch selects it; the toolbar then acts on it.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              key={col.id}
              className={`stui-theme-col${active && active.id === col.id ? ' active' : ''}`}
              onClick={() => setActiveId(col.id)}
            >
              <div className="stui-theme-col-head">
                <button type="button" className="stui-theme-col-title" title={col.label} onClick={() => setActiveId(col.id)}>
                  {col.label}
                </button>
                {badgesFor(col.theme)}
              </div>
              <ThemePreview tokens={col.tokens || {}} analyze={analyze} page={previewPage} onPage={setPreviewPage} />
            </div>
          ))}
        </div>
      </div>

      {canManage && builder && (
        <ThemeBuilder
          builder={builder}
          setBuilder={setBuilder}
          dirty={builderDirty}
          onSave={saveTheme}
          onClose={closeBuilder}
          onDelete={() => builderTheme && setDialog({ type: 'delete', theme: builderTheme })}
          onApplyTo={() => builderTheme && setDialog({ type: 'apply', theme: builderTheme })}
          saving={savingTheme}
          error={builderError}
          analyze={analyze}
          categories={categories}
          organizations={organizations}
        />
      )}

      {dialog?.type === 'delete' && (
        <DeleteThemeDialog theme={dialog.theme} themes={themes} uid={uid} onDone={dialogDone} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === 'apply' && (
        <ApplyToDialog theme={dialog.theme} themes={themes} uid={uid} onDone={dialogDone} onClose={() => setDialog(null)} />
      )}
    </>
  );
};

export default ThemesTab;
